// Feature flag types and interfaces
export type UserRole = 'admin' | 'manager' | 'driver' | 'owner' | 'guest';
export type Environment = 'development' | 'staging' | 'production';

export interface UserTargeting {
  userTypes?: UserRole[];
  userIds?: string[];
  regions?: string[];
  rolloutPercentage: number;
}

export interface FeatureFlag {
  enabled: boolean;
  description: string;
  rolloutPercentage: number;
  userTargeting?: UserTargeting;
  dependencies?: string[];
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt?: string;
}

export interface FeatureFlagConfig {
  [key: string]: FeatureFlag;
}

export interface FeatureFlagContextType {
  flags: FeatureFlagConfig;
  isFeatureEnabled: (featureKey: string, userId?: string, userType?: string) => boolean;
  getFeatureFlag: (featureKey: string) => FeatureFlag | undefined;
  updateFeatureFlag: (featureKey: string, updates: Partial<FeatureFlag>) => Promise<void>;
  refreshFlags: () => Promise<void>;
  environment: Environment;
}

// Core feature constants - 4 Primary Pages
export const CORE_FEATURES = {
  // Primary Page Features
  ENHANCED_DASHBOARD: 'enhanced_dashboard',
  ADVANCED_DRIVER_MANAGEMENT: 'advanced_driver_management', 
  COMPREHENSIVE_VEHICLE_TRACKING: 'comprehensive_vehicle_tracking',
  DETAILED_ASSIGNMENT_HISTORY: 'detailed_assignment_history',
  
  // Supporting Features
  REAL_TIME_ANALYTICS: 'real_time_analytics',
  MOBILE_OPTIMIZATION: 'mobile_optimization',
  
  // Legacy mappings for backward compatibility
  DASHBOARD_ENHANCED: 'enhanced_dashboard',
  DRIVERS_ENHANCED: 'advanced_driver_management',
  VEHICLES_ENHANCED: 'comprehensive_vehicle_tracking',
  ASSIGNMENT_HISTORY_ENHANCED: 'detailed_assignment_history',
} as const;

export type CoreFeatureKey = typeof CORE_FEATURES[keyof typeof CORE_FEATURES];

// Import the environment override
import { ENVIRONMENT_OVERRIDE } from './environmentOverride';

// Utility function to get current environment
export const getCurrentEnvironment = (): Environment => {
  // Check for manual override first (for testing)
  if (ENVIRONMENT_OVERRIDE) {
    console.log(`🔧 Environment Override Active: ${ENVIRONMENT_OVERRIDE}`);
    return ENVIRONMENT_OVERRIDE;
  }
  
  // In React Native, you can use different methods to determine environment
  if (__DEV__) {
    return 'development';
  }
  
  // You can add more sophisticated environment detection here
  // For example, based on app bundle ID, API endpoints, etc.
  return 'production';
};
